import { useQuery } from "@apollo/client/react";
import { Link } from "react-router";
import { FormattedMessage } from "react-intl";
import { buttonClasses } from "@/components/design/button-classes";
import { ActiveQuestionnairesByElementDocument } from "@/graphql/generated/operations";

// Grow · Power detail "Fragebögen" section. Lists the active questionnaires
// (QNR_*) bound to this creativity element, each with a per-row answered flag.
// Answering happens on the dedicated, self-assessment-style page
// (QuestionnaireAssessmentPage) the buttons link to. Empty is the normal case
// while no questionnaires are configured for the element.

interface Props {
  // CreativityElement enum value (e.g. "PERSEVERANCE"); cast at the query.
  element: string;
  // The route code (e.g. "SOR") used to build the assessment-page link.
  abbr: string;
  powerName: string;
}

export function PowerQuestionnaires({ element, abbr, powerName }: Props) {
  const { data, loading } = useQuery(ActiveQuestionnairesByElementDocument, {
    variables: { element: element as never },
    fetchPolicy: "cache-and-network",
  });
  const list = data?.activeQuestionnairesByElement ?? [];

  return (
    <section className="box bg-surface p-6">
      <div className="text-[10px] tracking-[0.25em] text-ink/55">
        <FormattedMessage id="grow.powerQuestionnaires.heading" defaultMessage="Questionnaires" />
      </div>
      {loading && !data ? (
        <div className="mt-2 text-[12px] text-ink/45">
          <FormattedMessage id="grow.powerQuestionnaires.loading" defaultMessage="loading…" />
        </div>
      ) : list.length === 0 ? (
        <p className="mt-2 text-sm text-ink/55">
          <FormattedMessage
            id="grow.powerQuestionnaires.empty"
            defaultMessage='No questionnaires linked to "{powerName}" yet.'
            values={{ powerName }}
          />
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {list.map((q) => {
            const id = q.questionnaire.questionnaireId;
            return (
              <li
                key={id}
                className="box-flat flex items-center justify-between gap-3 bg-page/40 p-3"
              >
                <div className="min-w-0">
                  <div className="text-[13px] font-medium">{q.questionnaire.name}</div>
                  {q.questionnaire.instructions ? (
                    <div className="text-[11px] leading-snug text-ink/55">
                      {q.questionnaire.instructions}
                    </div>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {q.answered ? (
                    <span className="field-label text-accent">
                      <FormattedMessage
                        id="grow.powerQuestionnaires.completed"
                        defaultMessage="completed"
                      />
                    </span>
                  ) : null}
                  <Link
                    to={`/grow/power/${abbr}/questionnaire/${id}`}
                    className={buttonClasses({
                      variant: "primary",
                      className: "px-3 py-1.5 text-[11px]",
                    })}
                  >
                    {q.answered ? (
                      <FormattedMessage
                        id="grow.powerQuestionnaires.repeat"
                        defaultMessage="Repeat"
                      />
                    ) : (
                      <FormattedMessage
                        id="grow.powerQuestionnaires.answer"
                        defaultMessage="Answer"
                      />
                    )}
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
